export type PreviewDocType = 'pdf' | 'docx' | 'xlsx' | 'csv';

// A resolved, ready-to-fetch file — the output of useDocPreview's SAS lookup,
// and DocPreviewShell's only input. Nothing upstream of this is hardcoded;
// sasUrl/fileName/contentType all come from the chat backend at runtime.
export interface PreviewFile {
  // Identity, matching the originating ChatResponseFile.path — used to tell
  // which of several files in one message is currently open (chat file
  // cards) and to key the renderer's ErrorBoundary. Deliberately path, not
  // file_id: file_id can be null on history rows, so it isn't reliably
  // unique — path always is (see Chat/types.tsx).
  path: string;
  sasUrl: string;
  type: PreviewDocType;
  fileName: string;
  contentType: string;
}

// The chat backend's file_type values for the four types this component
// renders. Anything else (image/*, etc.) isn't this component's concern —
// it's handled by a separate, already-shipped pipeline.
const MIME_TO_PREVIEW_TYPE: Record<string, PreviewDocType> = {
  'application/pdf': 'pdf',
  'application/docx': 'docx',
  'application/xlsx': 'xlsx',
  'text/csv': 'csv',
};

export function mapMimeToPreviewType(mimeType: string): PreviewDocType | null {
  return MIME_TO_PREVIEW_TYPE[mimeType] ?? null;
}

export interface FileTypeMeta {
  icon: string;     // FontAwesome class
  color: string;    // the file's brand-ish color, used on both the chat card and the preview header
  category: string; // shown as "Document · PDF" / "Table · CSV" on the chat card
}

// Single source of truth for how each type is presented anywhere in the UI
// (chat file cards, the preview panel header) — defined once here so the
// two places can't quietly drift out of sync with each other.
export const FILE_TYPE_META: Record<PreviewDocType, FileTypeMeta> = {
  pdf: { icon: 'fas fa-file-pdf', color: '#DC2626', category: 'Document' },
  docx: { icon: 'fas fa-file-word', color: '#2563EB', category: 'Document' },
  xlsx: { icon: 'fas fa-file-excel', color: '#16A34A', category: 'Table' },
  csv: { icon: 'fas fa-file-csv', color: '#0891B2', category: 'Table' },
};
